import { Box, Stack, Typography } from '@mui/material'
import { CORAL_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { Trans, useTranslation } from 'react-i18next'
import { useCallback, useMemo, useState } from 'react'

import { APIRequestErrorType } from 'constants/API'
import { KeyboardEventKey } from 'constants/misc'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import Modal from 'components/common/Modals/Modal/Modal'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { WorkspaceMember } from 'models/user'
import classnames from 'classnames'
import { decideErrorType } from 'utils/APIDecideErrorType'
import popupStyles from 'components/styles/popup.module.sass'
import styles from './RemoveMemberWithTransferPopup.module.sass'
import { useRemoveAndTransferWorkspaceMember } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useWorkspacesProfileContext } from '../_main/WorkspacesProfile.context'

/**
 * Returns the list of members eligible to receive a transfer, excluding the
 * member currently being removed.
 *
 * Matching is primarily done by `email` because the removal mutation payload
 * is keyed on email (`userToRemoveEmail` / `transferToUserEmail`) and the
 * backend rejects the transfer with HTTP 422 precisely when those two emails
 * are equal. Filtering by email is therefore the exact inverse of the backend
 * guard and is immune to `userId` drift between the two data sources (the
 * dropdown is built from the user-workspaces aggregate endpoint while the
 * member being removed comes from `GET /workspace/{id}/members`). `userId` is
 * retained as a secondary match so a stale list still excludes the right user.
 * Email comparison is case- and whitespace-insensitive.
 */
export function getTransferOptions(
  workspaceMembers: WorkspaceMember[] | undefined,
  memberToRemove: WorkspaceMember
): WorkspaceMember[] {
  const removedEmail = memberToRemove.email?.trim().toLowerCase()
  const removedUserId = memberToRemove.userId

  return workspaceMembers?.filter((m) => {
    const sameEmail = !!removedEmail && m.email?.trim().toLowerCase() === removedEmail
    const sameUserId = !!removedUserId && m.userId === removedUserId

    return !sameEmail && !sameUserId
  }) ?? []
}

interface Props {
  className?: string
  isOpen: boolean
  workspaceId: string
  member: WorkspaceMember
  onClickOutsideOrClose?: (e: React.MouseEvent) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  callbackAfterSuccessfulResponse?: () => void
}

export const RemoveMemberWithTransferPopup: React.FC<Props> = ({
  className = '',
  isOpen,
  workspaceId,
  member,
  onClickOutsideOrClose,
  onKeyDown,
  callbackAfterSuccessfulResponse,
}) => {
  const { t } = useTranslation(['workspace_profile', 'common'])
  const { sortedWorkspaceMembers } = useWorkspacesProfileContext()
  const removeAndTransfer = useRemoveAndTransferWorkspaceMember()
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  const [selectedTransferMember, setSelectedTransferMember] = useState<WorkspaceMember | undefined>()
  const [reason, setReason] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Filter out the member being removed from transfer options (match by email, the backend guard's identity; userId secondary)
  const transferOptions = useMemo(() =>
    getTransferOptions(sortedWorkspaceMembers, member)
    , [sortedWorkspaceMembers, member])

  const resetState = useCallback(() => {
    setSelectedTransferMember(undefined)
    setReason('')
  }, [])

  const onOutsideOrClose = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
    if (onClickOutsideOrClose) onClickOutsideOrClose(e)
    removeAndTransfer.reset()
    resetState()
  }, [removeAndTransfer, onClickOutsideOrClose, resetState])

  const onKeyboardEvent = useCallback((e: React.KeyboardEvent) => {
    if (onKeyDown) onKeyDown(e)
    if (onKeyDown && e.key === KeyboardEventKey.ESCAPE) {
      removeAndTransfer.reset()
    }
  }, [removeAndTransfer, onKeyDown])

  const handleConfirm = useCallback(() => {
    if (!selectedTransferMember) return

    removeAndTransfer.mutate({
      workspaceId,
      userToRemoveEmail: member.email,
      transferToUserEmail: selectedTransferMember.email,
      ...(reason && { reason })
    }, {
      onSuccess: () => {
        spawnSuccessToast(t('transfer_success'))
        callbackAfterSuccessfulResponse?.()
        removeAndTransfer.reset()
        resetState()
      },
      onError: (error) => {
        const errorMessage = decideErrorType(error) === APIRequestErrorType.PAYMENT_REQUIRED_ERROR
          ? t('error_payment_required')
          : t('transfer_error')
        spawnErrorToast(errorMessage)
        removeAndTransfer.reset()
      }
    })
  }, [removeAndTransfer, workspaceId, member.email, selectedTransferMember, reason, spawnSuccessToast, spawnErrorToast, t, callbackAfterSuccessfulResponse, resetState])

  const isPending = removeAndTransfer.isPending
  const isConfirmDisabled = !selectedTransferMember || isPending

  return (
    <Modal
      className={classnames(styles.removeMemberWithTransferPopup, className)}
      isOpen={isOpen}
      modalContentClassName={classnames(popupStyles.modalContentSmall, styles.content)}
      onClickOutside={onOutsideOrClose}
      onKeyDown={onKeyboardEvent}
      onClose={onOutsideOrClose}
      title={t('transfer_and_remove_title')}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap={1}>
          <MUIButton
            type="secondaryBorder"
            disabled={isPending}
            onClick={onOutsideOrClose}
          >
            {t('common:Cancel')}
          </MUIButton>
          <MUIButton
            type="orangePrimary"
            disabled={isConfirmDisabled}
            isLoading={isPending}
            onClick={handleConfirm}
          >
            {t('transfer_and_remove_button')}
          </MUIButton>
        </Stack>
      }
    >
      <div className={popupStyles.inside}>
        {/* Warning message */}
        <Stack direction="row" gap="1rem" alignItems="flex-start" marginBottom="1.6rem">
          <WarningAmberRoundedIcon sx={{ color: CORAL_600, fontSize: '2.4rem' }} />
          <Stack gap=".4rem">
            <Typography variant="text-sm" fontWeight="semiBold" color={CORAL_600}>
              {t('transfer_and_remove_warning')}
            </Typography>
            <Typography variant="text-sm" color={GRAY_700}>
              <Trans
                t={t}
                i18nKey="transfer_and_remove_description"
                values={{ memberName: member.name }}
                components={{ strong: <strong /> }}
              />
            </Typography>
          </Stack>
        </Stack>

        {/* Member selection dropdown */}
        <Box marginBottom="1.2rem">
          <MUIDropdown
            open={isDropdownOpen}
            onClose={() => setIsDropdownOpen(false)}
            placement="bottom-start"
            sx={{ '& .MuiPaper-root': { maxHeight: '20rem' } }}
            button={() => (
              <MUIDropdownInput
                type={DropdownInputTypes.SELECTOR}
                label={t('transfer_to_label')}
                value={selectedTransferMember ? `${selectedTransferMember.name} (${selectedTransferMember.email})` : ''}
                placeholder={t('transfer_to_placeholder')}
                readOnly
                disabled={isPending}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              />
            )}
          >
            {transferOptions.map((workspaceMember) => {
              const { userId, name, email } = workspaceMember

              return (
                <MUIDropdownItem
                  key={userId}
                  isSelected={selectedTransferMember?.userId === userId}
                  onClick={() => {
                    setSelectedTransferMember(workspaceMember)
                    setIsDropdownOpen(false)
                  }}
                >
                  <Stack gap=".2rem">
                    <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                      {name}
                    </Typography>
                    <Typography variant="text-sm" color={GRAY_700}>
                      {email}
                    </Typography>
                  </Stack>
              </MUIDropdownItem>
              )
            })}
          </MUIDropdown>
        </Box>

        {/* Optional reason field */}
        <Box marginBottom="1.6rem">
          <MUIInputField
            label={t('reason_label')}
            value={reason}
            placeholder={t('reason_placeholder')}
            isMultiline
            disabled={isPending}
            onChange={(e) => setReason(e.target.value)}
          />
        </Box>
      </div>
    </Modal>
  )
}
